import cloneDeep from 'lodash-es/cloneDeep'

class Token {
  _type
  _id
}

export class WordToken extends Token {
  start = 0
  duration = 0
  word = ''
  done = false
  constructor(wordText) {
    super(wordText)
    this._type = 'WordToken'
    this._id = `${Date.now()}-${Math.random()}`
    this.word = wordText
  }
}

// 各种特殊符号的 unicode 码查询 https://unicode-table.com/cn/blocks/latin-1-supplement/
const prefixReg = /^[“‘【[(（)]+$/ // 一般放在字词前面的符号
const suffixReg = /^[ ”’）)?？】\]!！：:,，。;；、]+$/ // 包括空格，其他一般跟在字词后面的符号
const middleReg = /^['@\\/+\-*&|.]+$/ // 连字符号
const alphaReg = /^[a-zA-Z0-9а-яА-Яà-ɏ]+$/ // 英文字母、俄文字母、带音标的字符

export default class LyricAST {
  defaultOptions = {
    autoAST: false
  }

  lyricText = ''
  ASTList = []
  sentencesTextList = []
  sentencesWordsList = []
  sentencesTimeList = [] // [[开始时间, 持续时间], []] 用来保存每一句的开始时间和持续时间
  sentencesLength = 0
  songInfo = {} // 歌曲信息
  tlsAST = null // 音译 AST 对象

  /**
   *
   * @param {String} lyricText 歌词纯文本信息
   * @param {Object} songInfo 歌曲信息对象
   * @param {String} songInfo.trackName 歌曲名
   * @param {String} songInfo.singerName 歌手名
   * @param {String} songInfo.albumName 专辑名
   * @param {String} songInfo.author 歌词制作者名
   * @param {Object} options 可选项
   * @param {Boolean} options.autoAST 自动解析文本
   */
  constructor(lyricText = '', songInfo = {}, options = {}) {
    // if (lyricText && typeof lyricText !== 'string') {
    //   throw TypeError('参数不正确，{lyricText}必须是字符串类型')
    // }

    const opts = Object.assign({}, this.defaultOptions, options)
    this.options = opts
    this.songInfo = songInfo
    this.originOptions = cloneDeep(options)
    this.lyricText = lyricText

    if (lyricText && opts.autoAST) {
      this.toAST()
    }
  }

  setLineStart(lineIndex, time) {
    this.sentencesTimeList[lineIndex][0] = time
  }

  setLineDuration(lineIndex, time) {
    this.sentencesTimeList[lineIndex][1] = time
  }

  setWordStart(lineIndex, wordIndex, time) {
    this.ASTList[lineIndex][wordIndex].start = time
  }

  setWordDuration(lineIndex, wordIndex, time) {
    this.ASTList[lineIndex][wordIndex].duration = time
  }

  setLineWords(lineIndex, lineWords) {
    this.sentencesWordsList.splice(lineIndex, 1, lineWords)
    this.ASTList.splice(lineIndex, 1, lineWords.map(word => new WordToken(word)))
  }

  /**
   *
   * @param {Number} lineIndex 行号
   * @param {Number} wordIndex 字号
   * @param {Boolean} status 是否完成
   */
  setWordDone(lineIndex, wordIndex, status) {
    this.ASTList[lineIndex][wordIndex].done = status
  }

  getASTList() {
    return this.ASTList
  }

  getASTLength() {
    return this.ASTList.length
  }

  getASTLine(lineIndex) {
    return this.ASTList[lineIndex]
  }

  getWord(lineIndex, wordIndex) {
    return this.ASTList[lineIndex][wordIndex]
  }

  getWordText(lineIndex, wordIndex) {
    return this.ASTList[lineIndex][wordIndex].word
  }

  getWordStart(lineIndex, wordIndex) {
    return this.ASTList[lineIndex][wordIndex].start
  }

  getWordDuration(lineIndex, wordIndex) {
    return this.ASTList[lineIndex][wordIndex].duration
  }

  getWordEnd(lineIndex, wordIndex) {
    const wordToken = this.ASTList[lineIndex][wordIndex]
    return wordToken.start + wordToken.duration
  }

  getWordDone(lineIndex, wordIndex) {
    return this.ASTList[lineIndex][wordIndex].done
  }

  getLine(lineIndex) {
    return this.ASTList[lineIndex]
  }

  getLineText(lineIndex) {
    return this.sentencesTextList[lineIndex]
  }

  getLineWords(lineIndex) {
    return this.sentencesWordsList[lineIndex]
  }

  getLineStart(lineIndex) {
    return this.sentencesTimeList[lineIndex][0]
  }

  getLineDuration(lineIndex) {
    return this.sentencesTimeList[lineIndex][1]
  }

  getSentencesTimeList() {
    return this.sentencesTimeList
  }

  getLineEnd(lineIndex) {
    const lineTime = this.sentencesTimeList[lineIndex]
    return lineTime[0] + lineTime[1]
  }

  getLineLength(lineIndex) {
    return this.ASTList[lineIndex].length
  }

  deleteLine(lineIndex) {
    this.ASTList.splice(lineIndex, 1)
    this.sentencesTextList.splice(lineIndex, 1)
    this.sentencesLength = this.sentencesLength - 1
    this.sentencesWordsList.splice(lineIndex, 1)
    this.sentencesTimeList.splice(lineIndex, 1)

    // 重新生成新的 text，这样回退到上一步看到的文本也会丢失当前行
    // this.lyricText = this.sentencesTextList.join('\n')
  }

  replaceLine(lineIndex, lineWordList, lineAST) {
    this.ASTList.splice(lineIndex, 1, lineAST)
    this.sentencesTextList.splice(lineIndex, 1, lineWordList.join(''))
    this.sentencesWordsList.splice(lineIndex, 1, lineWordList)

    // 重新生成新的 text，这样回退到上一步看到的文本也会是最新的
    // this.lyricText = this.sentencesTextList.join('\n')
  }

  getWordList(lineText) {
    return this._wordAnalyzer2(lineText)
  }

  getLineAST(lineText) {
    const wordList = this._wordAnalyzer2(lineText)

    const sentenceAST = wordList.map(wordText => {
      return this._wrapWord(wordText)
    })

    return sentenceAST
  }

  /**
   *
   * @param {Object} translator 音译器对象
   */
  transliterate(translator) {
    for (let i = 0; i < this.ASTList.length; i++) {
      const line = this.ASTList[i]
      for (let j = 0; j < line.length; j++) {
        const wordObj = line[j]
        wordObj.word = translator.translate(wordObj.word)
        this.sentencesWordsList[i][j] = wordObj.word
      }
    }
  }

  /**
   * 文本转为 lyricAST 的逻辑
   * 1、先断句，利用换行符断句
   * 2、以空格断词
   * 3、判断词是否全是英文，如果是则是一个词，如果不是再下一步
   * 4、以 ’‘ 再把 词 断开，
   */
  toAST() {
    if (this.lyricText === '') return this.trcAST

    const lyricText = this.lyricText
    const sentence = this._sentenceAnalyzer(lyricText)

    // this.ASTList = [
    //   [{ word: '潘', start: 0, duration: 0 }, { word: '玮', start: 0, duration: 0 }, { word: '柏', start: 0, duration: 0 },
    //     { word: '邓', start: 0, duration: 0 }, { word: '紫', start: 0, duration: 0 }, { word: '棋', start: 0, duration: 0 },
    //     { word: '战', start: 0, duration: 0 }, { word: '队', start: 0, duration: 0 }, { word: '“现', start: 0, duration: 0 },
    //     { word: '实”', start: 0, duration: 0 }, { word: 'VS ', start: 0, duration: 0 }, { word: '吴', start: 0, duration: 0 }],
    //   [{ word: 'beat1', start: 0, duration: 0 }, { word: '编', start: 0, duration: 0 }, { word: '曲：', start: 0, duration: 0 }, { word: 'L-BRODY', start: 0, duration: 0 }],
    //   [{ word: 'beat1', start: 0, duration: 0 }, { word: '编', start: 0, duration: 0 }, { word: '曲：', start: 0, duration: 0 }, { word: '赫', start: 0, duration: 0 }, { word: '连', start: 0, duration: 0 }, { word: '长', start: 0, duration: 0 }, { word: '虹', start: 0, duration: 0 }],
    //   [{ word: 'beat1', start: 0, duration: 0 }, { word: '编', start: 0, duration: 0 }, { word: '曲：', start: 0, duration: 0 }, { word: 'superdeep', start: 0, duration: 0 }],
    //   [{ word: 'beat1', start: 0, duration: 0 }, { word: '编', start: 0, duration: 0 }, { word: '曲：', start: 0, duration: 0 }, { word: 'bfcat', start: 0, duration: 0 }, { word: '大', start: 0, duration: 0 }, { word: '肥', start: 0, duration: 0 }, { word: '猫', start: 0, duration: 0 }]
    // ]
    // this.sentencesTimeList = [
    //   [0, 0],
    //   [0, 0],
    //   [0, 0],
    //   [0, 0],
    //   [0, 0],
    // ]
    for (let i = 0; i < sentence.length; i++) {
      const s = sentence[i]
      const wordList = this._wordAnalyzer2(s)
      this.sentencesWordsList.push(wordList)

      const sentenceAST = wordList.map(wordText => {
        return this._wrapWord(wordText)
      })

      this.ASTList.push(sentenceAST)
      this.sentencesTimeList.push([0, 0])
    }
  }

  /**
   * 歌词断句
   *
   * @param {String} lyricText 歌词文本字符串
   */
  _sentenceAnalyzer(lyricText) {
    let sentence = lyricText.split(/\r|\n|\r\n/g)

    // 1、去掉每行前后的不可见字符
    // 2、剔除空行
    sentence = sentence.map(s => {
      return s.trim()
    }).filter(s => {
      return s !== ''
    })

    this.sentencesTextList = sentence
    this.sentencesLength = sentence.length

    return sentence
  }

  /**
   * 句子分词
   * 1、先以 空格 分割句子，得到一个数组
   *
   * @deprecated
   * @param {String} sentence 歌词句子字符串
   * @returns {Array}
   */
  _wordAnalyzer(sentence) {
    const result = []
    const wordList = []
    const letter = sentence.split('')

    // 空格补充回去上一个字符后面
    let tempStr = ''
    let tempLetter = ''
    for (let i = 0; i < letter.length; i++) {
      tempLetter = letter[i]
      tempStr += tempLetter.replace(/\s/, ' ') // 所有不可见字符都转为空格

      if (/[ /+]/.test(tempLetter)) {
        wordList.push(tempStr)
        tempStr = ''
      }
    }
    wordList.push(tempStr)

    for (let i = 0; i < wordList.length; i++) {
      let word = wordList[i]

      // 各种特殊符号的 unicode 码查询 https://unicode-table.com/cn/blocks/latin-1-supplement/
      // 英文字母、全角、半角、俄文
      // 带音标的拼音字符
      const noSplit = /^[\w()（）：’”'",.，!！[\]/+|；;&\-?@*、 а-яА-Яà-ɏ]+$/g.test(word)
      if (!noSplit) { // 包含英文字母或者指定符号算是一个英文单词
        // 不是英文，需要再分割开
        word = word.split('')
        result.push(...word)
        continue
      }

      result.push(word)
    }

    return result
  }

  /**
   *
   * @param {String} sentence 歌词句子字符串
   */
  _wordAnalyzer2(sentence) {
    const wordList = []
    const formatStr = sentence.replace(/\s/g, ' ')
    const letters = formatStr.split('')

    let word = ''
    let preNotMatch = false
    let hasPre = false
    for (let i = 0; i < letters.length; i++) {
      const letter = letters[i]

      if (suffixReg.test(letter)) {
        const nextLe = letters[i + 1]
        if (hasPre && (suffixReg.test(nextLe) || alphaReg.test(nextLe) || middleReg.test(nextLe))) {
          word += letter
          continue
        }
        if (word) { // 遇见后缀，并且前面有字了，把 前面的字+后缀组成词 加入 list
          wordList.push(word + letter)
          word = ''
          preNotMatch = false
          hasPre = false
          continue
        }
        word += letter
        continue
      }

      if (prefixReg.test(letter)) {
        if (word) { // 遇到前缀符号，把前面的所有当做一个词，再开始从新累积
          wordList.push(word)
          word = ''
        }
        hasPre = true
        preNotMatch = false
        word += letter
        continue
      }

      // 上一个字或上几个字没命中任何规则，这个字不是前缀也不是后缀，
      // 表示上一个字和这个字肯定需要分开，则先把上一个字直接加入 list
      if (preNotMatch) {
        wordList.push(word)
        word = ''
        hasPre = false
        preNotMatch = false
      }
      if (alphaReg.test(letter) || middleReg.test(letter)) { // 字母，或者中间符号，直接累加
        word += letter
        continue
      }

      if (word) { // 没命中任何规则，但是 word 存在
        if (hasPre) { // 是前缀符号，则相加作为一个词。一般这个情况的组合是 前缀 + 中/韩/日
          wordList.push(word + letter)
          word = ''
          preNotMatch = false
          hasPre = false
          continue
        }

        // 直接把 word 加入 list，一般这个情况的组合是 字母 + 中/韩/日
        wordList.push(word)
        word = ''
      }
      word += letter
      preNotMatch = true
    }
    if (word) wordList.push(word)

    // console.log(wordList)
    return wordList
  }

  _wrapWord(wordText) {
    return new WordToken(wordText)
  }
}
